import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Select,
  VStack,
  Grid,
  GridItem,
  useToast,
  useColorModeValue,
  Radio,
  RadioGroup,
  Stack,
  Text,
  Divider,
  FormHelperText,
  Collapse,
  Container,
  Heading,
  HStack,
  Icon,
} from "@chakra-ui/react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowBackIcon } from "@chakra-ui/icons";
import { employeeService } from "../employeeService";

const EmployeeFormPage = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;

  console.log("🔧 EmployeeFormPage rendered");
  console.log("🔧 URL params id:", id);
  console.log("🔧 Is edit mode:", isEdit);

  // Dark mode colors
  const bgColor = useColorModeValue("white", "gray.800");
  const disabledBg = useColorModeValue("gray.100", "gray.700");
  const mutedColor = useColorModeValue("gray.600", "gray.400");
  const containerBg = useColorModeValue("gray.50", "gray.900");

  const [formData, setFormData] = useState({
    employeeId: "",
    name: "",
    email: "",
    phone: "",
    department: "",
    position: "",
    status: "Active",
    dateOfJoining: "",
    salary: "",
    loginOption: "auto-generate", // Default to auto-generate password and email it
    password: "",
    role: "Employee",
  });

  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(isEdit);

  useEffect(() => {
    if (isEdit) {
      fetchEmployee();
    }
  }, [id]);

  const fetchEmployee = async () => {
    try {
      setPageLoading(true);
      console.log("🔧 Fetching employee with id:", id);
      const response = await employeeService.getEmployeeById(id);
      console.log("🔧 Employee data received:", response);
      console.log("🔧 Position from response:", response.position);

      setFormData({
        employeeId: response.employeeId || "",
        name: response.name || "",
        email: response.email || "",
        phone: response.phone || "",
        department: response.department || "",
        position: response.position || "",
        status: response.status || "Active",
        dateOfJoining: response.dateOfJoining
          ? new Date(response.dateOfJoining).toISOString().split("T")[0]
          : "",
        salary: response.salary || "",
        loginOption: "none", // Reset to default for edit mode
        password: "", // Clear password for security
        role: "Employee", // Reset to default
      });

      console.log("🔧 Form data set with position:", response.position);
    } catch (error) {
      console.error("🔧 Error fetching employee:", error);
      toast({
        title: "Error",
        description:
          error.response?.data?.message || "Failed to load employee data",
        status: "error",
        duration: 3000,
      });
      // Don't navigate away immediately - let user see the error
      setTimeout(() => navigate("/employees"), 2000);
    } finally {
      setPageLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const dataToSend = { ...formData };
      if (!isEdit) {
        delete dataToSend.employeeId;
      }

      // If loginOption is "none", don't send password and role to backend
      if (formData.loginOption === "none") {
        delete dataToSend.password;
        delete dataToSend.role;
      }

      console.log("📤 Data being sent to backend:", dataToSend);

      if (isEdit) {
        // Update employee data first
        const response = await employeeService.updateEmployee(id, dataToSend);

        let successMessage = `${formData.name} has been updated successfully`;

        // Handle password management separately if needed
        if (formData.loginOption && formData.loginOption !== "none") {
          try {
            const passwordResponse = await employeeService.managePassword(
              id,
              formData.loginOption,
              formData.password,
              formData.role,
            );

            if (passwordResponse.data?.tempPassword) {
              successMessage += `\n\nNew password generated! Temporary password: ${passwordResponse.data.tempPassword}\n(Password emailed to ${formData.email})`;
            } else if (passwordResponse.data?.inviteToken) {
              successMessage += `\n\nInvitation resent! Employee will receive an email to set their password.`;
            } else if (formData.loginOption === "manual-password") {
              successMessage += `\n\nNew password set! Employee can now login with the updated password.`;
            }
          } catch (passwordError) {
            console.error("Password management error:", passwordError);
            toast({
              title: "Password Update Failed",
              description:
                passwordError.response?.data?.message ||
                "Failed to update login credentials",
              status: "warning",
              duration: 4000,
            });
          }
        }

        toast({
          title: "Employee updated",
          description: successMessage,
          status: "success",
          duration: 6000,
          isClosable: true,
        });
      } else {
        const response = await employeeService.createEmployee(dataToSend);

        let successMessage = `${formData.name} has been added successfully`;

        if (response.data?.userCreated) {
          if (
            formData.loginOption === "auto-generate" &&
            response.data.tempPassword
          ) {
            successMessage += `\n\nLogin created! Temporary password: ${response.data.tempPassword}\n(Password emailed to ${formData.email})`;
          } else if (formData.loginOption === "manual-password") {
            successMessage += `\n\nLogin created! Employee can now login with the provided password.`;
          } else if (
            formData.loginOption === "invite-link" &&
            response.data.inviteToken
          ) {
            successMessage += `\n\nInvitation sent! Employee will receive an email to set their password.`;
          }
        }

        toast({
          title: "Employee created",
          description: successMessage,
          status: "success",
          duration: 6000,
          isClosable: true,
        });
      }

      navigate("/employees");
    } catch (error) {
      console.error("Error saving employee:", error);
      toast({
        title: isEdit ? "Error updating employee" : "Error creating employee",
        description:
          error.response?.data?.message ||
          error.message ||
          "Something went wrong",
        status: "error",
        duration: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  if (pageLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minH="100vh"
      >
        <Text>Loading...</Text>
      </Box>
    );
  }

  return (
    <Box bg={containerBg} minH="100vh" py={1}>
      <Container maxW="full">
        <Box bg={bgColor} borderRadius="xl" p={8} boxShadow="sm">
          {/* Header */}
          <HStack mb={8} justify="space-between">
            <VStack align="flex-start" spacing={2} flex="1">
              <HStack>
                <Button
                  variant="ghost"
                  leftIcon={<ArrowBackIcon />}
                  onClick={() => navigate("/employees")}
                  size="sm"
                >
                  Back
                </Button>
              </HStack>
              <Heading size="lg">
                {isEdit ? "Edit Employee" : "Create New Employee"}
              </Heading>
              <Text color={mutedColor} fontSize="sm">
                {isEdit
                  ? "Update employee information"
                  : "Add a new employee to your system"}
              </Text>
            </VStack>
          </HStack>

          <form onSubmit={handleSubmit}>
            <VStack spacing={6}>
              <Grid
                templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }}
                gap={6}
                width="100%"
              >
                {/* Employee ID - only show when editing */}
                {isEdit && (
                  <GridItem colSpan={{ base: 1, md: 2 }}>
                    <FormControl>
                      <FormLabel fontWeight="600">Employee ID</FormLabel>
                      <Input
                        name="employeeId"
                        value={formData.employeeId}
                        isDisabled
                        bg={disabledBg}
                        minH="44px"
                      />
                    </FormControl>
                  </GridItem>
                )}

                <GridItem colSpan={{ base: 1, md: isEdit ? 1 : 2 }}>
                  <FormControl isRequired>
                    <FormLabel fontWeight="600">Full Name</FormLabel>
                    <Input
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Enter full name"
                      minH="44px"
                    />
                  </FormControl>
                </GridItem>

                <GridItem>
                  <FormControl isRequired>
                    <FormLabel fontWeight="600">Email</FormLabel>
                    <Input
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="employee@company.com"
                      minH="44px"
                    />
                  </FormControl>
                </GridItem>

                <GridItem>
                  <FormControl>
                    <FormLabel fontWeight="600">Phone</FormLabel>
                    <Input
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="Enter phone number"
                      minH="44px"
                    />
                  </FormControl>
                </GridItem>

                <GridItem>
                  <FormControl isRequired>
                    <FormLabel fontWeight="600">Department</FormLabel>
                    <Select
                      name="department"
                      value={formData.department}
                      onChange={handleChange}
                      placeholder="Select department"
                      minH="44px"
                    >
                      <option value="Engineering">Engineering</option>
                      <option value="HR">HR</option>
                      <option value="Sales">Sales</option>
                      <option value="Marketing">Marketing</option>
                      <option value="Finance">Finance</option>
                      <option value="Operations">Operations</option>
                      <option value="IT">IT</option>
                      <option value="Customer Support">Customer Support</option>
                      <option value="Product">Product</option>
                      <option value="Design">Design</option>
                    </Select>
                  </FormControl>
                </GridItem>

                <GridItem>
                  <FormControl isRequired>
                    <FormLabel fontWeight="600">Position</FormLabel>
                    <Input
                      name="position"
                      value={formData.position}
                      onChange={handleChange}
                      placeholder="e.g., Software Engineer"
                      minH="44px"
                    />
                  </FormControl>
                </GridItem>

                <GridItem>
                  <FormControl isRequired>
                    <FormLabel fontWeight="600">Status</FormLabel>
                    <Select
                      name="status"
                      value={formData.status}
                      onChange={handleChange}
                      minH="44px"
                    >
                      <option value="Active">Active</option>
                      <option value="Inactive">Inactive</option>
                      <option value="On Leave">On Leave</option>
                    </Select>
                  </FormControl>
                </GridItem>

                <GridItem>
                  <FormControl>
                    <FormLabel fontWeight="600">Date of Joining</FormLabel>
                    <Input
                      name="dateOfJoining"
                      type="date"
                      value={formData.dateOfJoining}
                      onChange={handleChange}
                      minH="44px"
                    />
                  </FormControl>
                </GridItem>

                <GridItem colSpan={{ base: 1, md: 2 }}>
                  <FormControl>
                    <FormLabel fontWeight="600">Salary</FormLabel>
                    <Input
                      name="salary"
                      type="number"
                      value={formData.salary}
                      onChange={handleChange}
                      placeholder="Enter salary"
                      minH="44px"
                    />
                  </FormControl>
                </GridItem>

                {/* Login Account Options */}
                <GridItem colSpan={{ base: 1, md: 2 }}>
                  <Divider my={4} />
                  <Box>
                    <FormControl>
                      <FormLabel fontWeight="600">
                        {isEdit
                          ? "Reset/Manage Login Account"
                          : "Create Login Account for Employee"}
                      </FormLabel>
                      <RadioGroup
                        name="loginOption"
                        value={formData.loginOption}
                        onChange={(value) =>
                          setFormData((prev) => ({
                            ...prev,
                            loginOption: value,
                            password:
                              value === "manual-password" ? prev.password : "",
                          }))
                        }
                      >
                        <Stack spacing={4}>
                          <Radio
                            value="none"
                            minH="44px"
                            alignItems="flex-start"
                          >
                            <Text fontSize="sm">
                              <strong>No Login Account</strong> -{" "}
                              {isEdit
                                ? "Keep employee record only (no changes to system access)"
                                : "Employee record only (no system access)"}
                            </Text>
                          </Radio>
                          <Radio
                            value="auto-generate"
                            minH="44px"
                            alignItems="flex-start"
                          >
                            <Text fontSize="sm">
                              <strong>
                                {isEdit
                                  ? "Generate New Password"
                                  : "Auto-Generate Password"}
                              </strong>{" "}
                              -{" "}
                              {isEdit
                                ? "Create new random password and email it to employee"
                                : "System creates random password and emails it to employee"}
                            </Text>
                          </Radio>
                          <Radio
                            value="manual-password"
                            minH="44px"
                            alignItems="flex-start"
                          >
                            <Text fontSize="sm">
                              <strong>
                                {isEdit
                                  ? "Set New Password Manually"
                                  : "Set Password Manually"}
                              </strong>{" "}
                              -{" "}
                              {isEdit
                                ? "Provide a new password now"
                                : "You provide the password now"}
                            </Text>
                          </Radio>
                          <Radio
                            value="invite-link"
                            minH="44px"
                            alignItems="flex-start"
                          >
                            <Text fontSize="sm">
                              <strong>
                                {isEdit
                                  ? "Resend Invitation Link"
                                  : "Send Invitation Link"}
                              </strong>{" "}
                              -{" "}
                              {isEdit
                                ? "Send new invitation email for employee to set their own password (7-day expiry)"
                                : "Employee sets their own password via email link (7-day expiry)"}
                            </Text>
                          </Radio>
                        </Stack>
                      </RadioGroup>
                      <FormHelperText color={mutedColor} mt={2}>
                        {isEdit
                          ? "Update login credentials or resend invitation"
                          : "Choose how this employee will access the system"}
                      </FormHelperText>
                    </FormControl>

                    {/* Conditional Password Field */}
                    <Collapse
                      in={formData.loginOption === "manual-password"}
                      animateOpacity
                    >
                      <FormControl
                        isRequired={formData.loginOption === "manual-password"}
                        mt={4}
                      >
                        <FormLabel fontWeight="600">Password</FormLabel>
                        <Input
                          name="password"
                          type="password"
                          value={formData.password}
                          onChange={handleChange}
                          placeholder="Enter password for employee"
                          minH="44px"
                        />
                        <FormHelperText color={mutedColor}>
                          Employee will use this password to login
                        </FormHelperText>
                      </FormControl>
                    </Collapse>

                    {/* Role Selection */}
                    {formData.loginOption !== "none" && (
                      <FormControl isRequired mt={4}>
                        <FormLabel fontWeight="600">User Role</FormLabel>
                        <Select
                          name="role"
                          value={formData.role}
                          onChange={handleChange}
                          minH="44px"
                        >
                          <option value="Employee">Employee</option>
                          <option value="Manager">Manager</option>
                          <option value="HR">HR</option>
                          <option value="Admin">Admin</option>
                        </Select>
                        <FormHelperText color={mutedColor}>
                          Determines system permissions
                        </FormHelperText>
                      </FormControl>
                    )}
                  </Box>
                </GridItem>
              </Grid>

              {/* Form Actions */}
              <HStack gap={4} pt={4} justify="flex-end" w="full">
                <Button
                  variant="outline"
                  onClick={() => navigate("/employees")}
                  minH="44px"
                  px={8}
                >
                  Cancel
                </Button>
                <Button
                  colorScheme="blue"
                  type="submit"
                  isLoading={loading}
                  loadingText={isEdit ? "Updating..." : "Creating..."}
                  minH="44px"
                  px={8}
                >
                  {isEdit ? "Update Employee" : "Create Employee"}
                </Button>
              </HStack>
            </VStack>
          </form>
        </Box>
      </Container>
    </Box>
  );
};

export default EmployeeFormPage;
